import { useEffect, useMemo, useState } from 'react';
import { Search, ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  useAdminLeads,
  useDeleteLead,
  useSpamLead,
  useUpdateLead,
} from '../../hooks/useAdminLeads';
import type { LeadFilters, LeadStatus, AdminLead } from '../../api/adminLeads';

const leadTabs: Array<{ value: LeadStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'closed', label: 'Closed' },
  { value: 'spam', label: 'Spam' },
];

export function LeadManagement() {
  const [activeTab, setActiveTab] = useState<LeadStatus | 'all'>('new');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const filters = useMemo<LeadFilters>(
    () => ({
      status: activeTab === 'all' ? undefined : activeTab,
      query: debouncedSearch || undefined,
    }),
    [activeTab, debouncedSearch],
  );

  const { data, isLoading, isError, refetch, isRefetching } = useAdminLeads(filters);
  const updateLead = useUpdateLead(filters);
  const spamLead = useSpamLead(filters);
  const deleteLead = useDeleteLead(filters);

  const leads = data ?? [];

  return (
    <main className="min-h-screen bg-[#05060f] pb-24" aria-labelledby="lead-management-title">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[#f7ef8a]">Admin · Leads</p>
            <h1 id="lead-management-title" className="mt-2 text-3xl font-semibold text-white">Lead triage</h1>
            <p className="text-sm text-gray-400">Follow up with inbound employers, qualify opportunities, and keep spam at bay.</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              className="border-white/10 bg-white/[0.08] pl-9 text-sm text-white placeholder:text-gray-500"
              placeholder="Search name, email, company…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as LeadStatus | 'all')} className="space-y-6">
          <TabsList className="flex flex-wrap gap-2 border border-white/10 bg-white/5 p-2">
            {leadTabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="text-sm capitalize text-gray-200 data-[state=active]:bg-gold-gradient data-[state=active]:text-black"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={activeTab} className="space-y-4">
            {isError && (
              <Card className="border-red-500/40 bg-red-500/10">
                <CardContent className="flex items-center justify-between p-6 text-sm text-red-100">
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5" />
                    Unable to load leads right now.
                  </div>
                  <Button variant="outline" className="border-white/20 text-white" onClick={() => refetch()}>
                    Retry
                  </Button>
                </CardContent>
              </Card>
            )}

            {!isError && (isLoading || isRefetching) && (
              <Card className="border-white/10 bg-white/[0.02]">
                <CardContent className="p-8 text-sm text-gray-400">Loading latest leads…</CardContent>
              </Card>
            )}

            {!isLoading && !isError && leads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                onUpdate={async (status, note) => {
                  try {
                    await updateLead.mutateAsync({ id: lead.id, status, notes: note });
                    toast.success(`Lead marked ${status.replace('_', ' ')}.`);
                  } catch (error) {
                    console.error(error);
                    toast.error(error instanceof Error ? error.message : 'Unable to update lead.');
                  }
                }}
                onSpam={async () => {
                  try {
                    await spamLead.mutateAsync(lead.id);
                    toast.success('Lead marked as spam.');
                  } catch (error) {
                    console.error(error);
                    toast.error(error instanceof Error ? error.message : 'Unable to ban sender.');
                  }
                }}
                onDelete={async () => {
                  try {
                    await deleteLead.mutateAsync(lead.id);
                    toast.success('Lead removed.');
                  } catch (error) {
                    console.error(error);
                    toast.error(error instanceof Error ? error.message : 'Unable to delete lead.');
                  }
                }}
              />
            ))}

            {!isLoading && !isError && leads.length === 0 && (
              <Card className="border-white/10 bg-white/[0.02]">
                <CardContent className="p-12 text-center text-sm text-gray-400">
                  No leads match this filter yet. Keep an eye out for fresh submissions.
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}

type LeadCardProps = {
  lead: AdminLead;
  onUpdate: (status: LeadStatus, note?: string) => Promise<void>;
  onSpam: () => Promise<void>;
  onDelete: () => Promise<void>;
};

function LeadCard({ lead, onUpdate, onSpam, onDelete }: LeadCardProps) {
  const formattedCreated = useMemo(
    () => new Date(lead.createdAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }),
    [lead.createdAt],
  );
  const statusColors: Record<LeadStatus, string> = {
    new: 'bg-blue-500/20 text-blue-200',
    in_progress: 'bg-amber-500/20 text-amber-200',
    qualified: 'bg-emerald-500/20 text-emerald-200',
    closed: 'bg-gray-500/20 text-gray-200',
    spam: 'bg-red-500/20 text-red-200',
  };

  return (
    <Card className="border-white/10 bg-white/[0.03]">
      <CardHeader className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle className="text-xl text-white">{lead.company ?? lead.name}</CardTitle>
          <p className="text-sm text-gray-400">{lead.name} · {lead.email}</p>
          {lead.topic && <p className="text-xs uppercase tracking-[0.3em] text-gray-500">{lead.topic}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={`${statusColors[lead.status]} capitalize`}>{lead.status.replace('_', ' ')}</Badge>
          {lead.assignedTo && (
            <Badge className="border-white/10 bg-white/[0.07] text-gray-200">Assigned to {lead.assignedTo}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-6 pt-0">
        <div className="rounded-lg border border-white/10 bg-white/[0.05] p-4 text-sm text-gray-200">
          <p className="font-semibold text-white">Message</p>
          <p className="mt-2 whitespace-pre-line text-gray-300">{lead.message}</p>
        </div>
        {lead.notes && (
          <div className="rounded-lg border border-white/10 bg-[#f7ef8a]/5 p-4 text-sm text-[#f7ef8a]">
            <p className="font-semibold">Notes</p>
            <p className="mt-1 text-[#f7ef8a]/90">{lead.notes}</p>
          </div>
        )}
        <p className="text-xs text-gray-500">Created {formattedCreated}</p>
        <div className="flex flex-wrap gap-3">
          {lead.status !== 'in_progress' && (
            <Button
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              onClick={() => onUpdate('in_progress', 'Lead picked up by admin')}
            >
              Move to in progress
            </Button>
          )}
          {lead.status !== 'qualified' && lead.status !== 'spam' && (
            <Button className="bg-gold-gradient text-black hover:brightness-110" onClick={() => onUpdate('qualified')}>
              Mark qualified
            </Button>
          )}
          {lead.status !== 'closed' && lead.status !== 'spam' && (
            <Button variant="outline" className="border-emerald-400/40 text-emerald-200" onClick={() => onUpdate('closed')}>
              Close won
            </Button>
          )}
          {lead.status !== 'spam' && (
            <Button variant="ghost" className="text-red-300 hover:text-red-200" onClick={onSpam}>
              Mark spam
            </Button>
          )}
          <Button variant="ghost" className="text-gray-400 hover:text-gray-200" onClick={onDelete}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
